import React, { useEffect, useState, useCallback } from "react";
import { useQuery, useMutation, useLazyQuery } from "@apollo/client";
import jsPDF from "jspdf";
import "jspdf-autotable";
import * as XLSX from "xlsx";
import debounce from "lodash.debounce";
import {
    Button,
    TextField,
    Paper,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    IconButton,
    Box,
    Grid,
    Divider,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Tooltip,
    Typography,
    CircularProgress,
    Switch,
    FormControlLabel,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import SearchIcon from "@mui/icons-material/Search";
import { styled } from '@mui/material/styles';
import { useTranslation } from "react-i18next";

// Import queries and mutations
import { Items, GET_ITEM_BY_REF } from "../graphql/querys/items";
import { ADD_STOCK, REMOVE_STOCK } from "../graphql/mutation/stock";
import { CREATE_ITEM } from "../graphql/mutation/createItem";

// Custom styled switch
const CustomSwitch = styled(Switch)(({ theme }) => ({
    width: 62,
    height: 34,
    padding: 7,
    '& .MuiSwitch-switchBase': {
        margin: 1,
        padding: 0,
        transform: 'translateX(6px)',
        '&.Mui-checked': {
            color: '#fff',
            transform: 'translateX(22px)',
            '& + .MuiSwitch-track': {
                backgroundColor: '#1976d2',
                opacity: 1,
            }
        },
    },
    '& .MuiSwitch-thumb': {
        backgroundColor: '#1976d2',
        width: 32,
        height: 32,
    },
    '& .MuiSwitch-track': {
        opacity: 1,
        backgroundColor: '#aab4be',
        borderRadius: 20 / 2,
    },
}));

function ItemManager() {
    const { t } = useTranslation();
    // Search and dialog states
    const [term, setTerm] = useState("");
    const [open, setOpen] = useState(false);
    const [dialogCount, setDialogCount] = useState(1);
    const [selectedItemId, setSelectedItemId] = useState(null);
    const [actionType, setActionType] = useState(null);

    // Supplier info state
    const [supplierInfo, setSupplierInfo] = useState({
        supplier: "",
        supplier_nif: "",
        fatNr: "",
        date: new Date().toISOString().split('T')[0], // Default to today's date
    });

    // Items form state
    const [newItems, setNewItems] = useState([
        {
            ref: "",
            name: "",
            brand: "",
            count: 0,
            existing: false,
            priceCost: 0,
            iva: 23,
        },
    ]);

    // Main query for displaying list
    const { loading, data, refetch } = useQuery(Items, {
        variables: { term },
    });

    const [getItemByRef, { loading: refLoading }] = useLazyQuery(GET_ITEM_BY_REF, {
        fetchPolicy: "network-only",
        onError: (error) => {
            console.error("GET_ITEM_BY_REF error:", error.message);
        },
    });

    // Mutations
    const [createItem] = useMutation(CREATE_ITEM);
    const [addStock] = useMutation(ADD_STOCK);
    const [removeStock] = useMutation(REMOVE_STOCK);

    // Debounced setter for the search term
    const debouncedSetTerm = useCallback(
        debounce((value) => {
            setTerm(value);
        }, 300),
        []
    );

    // Dialog handlers
    const handleOpen = (id, action) => {
        setOpen(true);
        setSelectedItemId(id);
        setActionType(action);
    };

    const handleClose = () => {
        setOpen(false);
        setDialogCount(1);
    };

    const handleStockUpdate = async () => {
        if (actionType === "add") {
            await addStock({
                variables: { id: selectedItemId, count: Number(dialogCount) },
            });
        } else if (actionType === "remove") {
            await removeStock({
                variables: { id: selectedItemId, count: Number(dialogCount) },
            });
        }
        refetch();
        handleClose();
    };

    // Export to Excel handler
    const handleExportExcel = () => {
        if (!data || !data.items) {
            alert(t("COMMON.NO_DATA_EXPORT"));
            return;
        }
        const exportData = data.items.map((item) => ({
            codigo: item.ref,
            tipo: "m",
            descrição: item.name,
            unidade: "un",
            quantidade: item.count,
        }));
        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Items");
        XLSX.writeFile(workbook, "exported_items.xlsx");
    };

    // Enhanced PDF generation function
    // Enhanced PDF generation function in Portuguese
    const generatePDF = (items) => {
        // Initialize PDF with better default styling
        const doc = new jsPDF();
        const pageWidth = doc.internal.pageSize.width;
        const pageHeight = doc.internal.pageSize.height;
        const margin = 20;

        // Add a full-width header background
        doc.setFillColor(41, 128, 185); // Professional blue
        doc.rect(0, 0, pageWidth, 60, 'F');

        // Add decorative element
        doc.setFillColor(52, 152, 219); // Lighter blue
        doc.rect(0, 55, pageWidth, 5, 'F');

        // Company Logo/Name Section
        doc.setTextColor(255, 255, 255);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(28);
        doc.text(t("ITEMS.REPORT_TITLE"), margin, 35);

        // Date and Report Info
        doc.setFontSize(12);
        doc.setFont("helvetica", "normal");
        const dateStr = new Date(supplierInfo.date).toLocaleDateString('pt-PT', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
        doc.text(t("ITEMS.REPORT_GENERATED_AT", { date: dateStr }), pageWidth - margin - 60, 35);

        // Supplier Information Section with modern card-like design
        doc.setDrawColor(230, 230, 230);
        doc.setFillColor(249, 249, 249);
        doc.roundedRect(margin, 80, pageWidth - (margin * 2), 45, 3, 3, 'F');

        // Supplier Info Header
        doc.setTextColor(41, 128, 185);
        doc.setFontSize(14);
        doc.setFont("helvetica", "bold");
        doc.text(t("ITEMS.SUPPLIER_DETAILS"), margin + 10, 95);

        // Supplier Details
        doc.setTextColor(80, 80, 80);
        doc.setFontSize(11);
        doc.setFont("helvetica", "normal");
        doc.text(`${t("FIELDS.supplier")}: ${supplierInfo.supplier}`, margin + 10, 105);
        doc.text(`${t("FIELDS.nif")}: ${supplierInfo.supplier_nif}`, margin + 10, 113);
        doc.text(`${t("FIELDS.invoiceNumber")}: ${supplierInfo.fatNr}`, margin + 10, 121);

        // Process items and ensure IVA is set to 23% when not specified
        const processedItems = items.map(item => ({
            ...item,
            iva: item.iva || 23 // Set default IVA to 23% if not specified
        }));

        // Summary Statistics Card
        const stats = {
            totalItems: processedItems.reduce((sum, item) => sum + item.count, 0),
            totalValue: processedItems.reduce((sum, item) => {
                const priceCost = parseFloat(item.priceCost || 0);
                const iva = parseFloat(item.iva || 23); // Default 23%
                return sum + (priceCost * item.count * (1 + iva / 100));
            }, 0),
            uniqueItems: processedItems.length,
            get averageValue() {
                return this.totalValue / this.uniqueItems || 0;
            }
        };

        // Add summary statistics in a modern grid layout
        doc.setFillColor(245, 247, 250);
        doc.roundedRect(margin, 135, pageWidth - (margin * 2), 40, 3, 3, 'F');

        // Statistics grid
        const statGridX = margin + 10;
        let statGridY = 145;

        // Helper function for stat items
        const addStatItem = (label, value, x, y) => {
            doc.setTextColor(100, 100, 100);
            doc.setFontSize(10);
            doc.text(label, x, y);
            doc.setTextColor(41, 128, 185);
            doc.setFontSize(14);
            doc.setFont("helvetica", "bold");
            doc.text(value, x, y + 8);
            doc.setFont("helvetica", "normal");
        };

        // Add statistics in a grid
        const colWidth = (pageWidth - (margin * 2) - 20) / 2;
        addStatItem(t("ITEMS.TOTAL_ITEMS"), stats.totalItems.toString(), statGridX, statGridY);
        addStatItem(t("ITEMS.TOTAL_VALUE"), `€${stats.totalValue.toFixed(2)}`, statGridX + colWidth, statGridY);
        addStatItem(t("ITEMS.UNIQUE_ITEMS"), stats.uniqueItems.toString(), statGridX, statGridY + 20);
        addStatItem(t("ITEMS.AVERAGE_VALUE"), `€${stats.averageValue.toFixed(2)}`, statGridX + colWidth, statGridY + 20);

        // Items Table with enhanced styling
        doc.autoTable({
            startY: 190,
            head: [[
                t("FIELDS.ref"),
                t("FIELDS.name"),
                t("FIELDS.brand"),
                t("FIELDS.count"),
                t("ITEMS.UNIT_PRICE"),
                t("FIELDS.iva"),
                t("ITEMS.TOTAL"),
            ]],
            body: processedItems.map(item => {
                const priceCost = parseFloat(item.priceCost) || 0;
                const iva = parseFloat(item.iva || 23); // Default 23%
                const totalWithIVA = priceCost * item.count * (1 + iva / 100);
                return [
                    item.ref,
                    item.name,
                    item.brand,
                    item.count,
                    `€${priceCost.toFixed(2)}`,
                    `${iva}%`,
                    `€${totalWithIVA.toFixed(2)}`
                ];
            }),
            styles: {
                fontSize: 9,
                cellPadding: 6,
                lineColor: [220, 220, 220],
                lineWidth: 0.5,
            },
            headStyles: {
                fillColor: [41, 128, 185],
                textColor: [255, 255, 255],
                fontSize: 10,
                fontStyle: 'bold',
                halign: 'center'
            },
            alternateRowStyles: {
                fillColor: [245, 247, 250]
            },
            columnStyles: {
                0: { cellWidth: 30 },
                1: { cellWidth: 'auto' },
                2: { cellWidth: 30 },
                3: { cellWidth: 25, halign: 'center' },
                4: { cellWidth: 25, halign: 'right' },
                5: { cellWidth: 20, halign: 'center' },
                6: { cellWidth: 25, halign: 'right' }
            },
        });

        // Final Totals Section after the table
        const finalY = doc.lastAutoTable.finalY + 20;

        // Add totals in a modern card
        doc.setFillColor(41, 128, 185);
        doc.roundedRect(pageWidth - 100 - margin, finalY, 100, 50, 3, 3, 'F');

        // Calculate totals
        const totalLiquid = processedItems.reduce((sum, item) =>
            sum + (parseFloat(item.priceCost || 0) * item.count), 0
        );

        const totalIVA = processedItems.reduce((sum, item) => {
            const priceCost = parseFloat(item.priceCost || 0);
            const iva = parseFloat(item.iva || 23); // Default 23%
            return sum + (priceCost * item.count * iva / 100);
        }, 0);

        const totalWithIVA = totalLiquid + totalIVA;

        // Add totals with white text
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(10);
        doc.text(t("ITEMS.NET_TOTAL"), pageWidth - 90 - margin, finalY + 15);
        doc.text(t("ITEMS.VAT_TOTAL"), pageWidth - 90 - margin, finalY + 25);
        doc.setFontSize(12);
        doc.setFont("helvetica", "bold");
        doc.text(t("ITEMS.GRAND_TOTAL"), pageWidth - 90 - margin, finalY + 40);

        // Add amounts
        doc.setFont("helvetica", "normal");
        doc.text(`€${totalLiquid.toFixed(2)}`, pageWidth - 25 - margin, finalY + 15, { align: "right" });
        doc.text(`€${totalIVA.toFixed(2)}`, pageWidth - 25 - margin, finalY + 25, { align: "right" });
        doc.setFont("helvetica", "bold");
        doc.text(`€${totalWithIVA.toFixed(2)}`, pageWidth - 25 - margin, finalY + 40, { align: "right" });

        // Footer
        const footerY = finalY + 70;
        doc.setTextColor(100, 100, 100);
        doc.setFontSize(8);
        doc.setFont("helvetica", "italic");
        doc.text(t("ITEMS.INTERNAL_REPORT_NOTICE"),
            pageWidth / 2, footerY, { align: "center" });

        // Save the PDF
        doc.save(`relatorio_inventario_${supplierInfo.fatNr || "stock"}.pdf`);
    };

    // Form row handlers
    const handleAddRow = () => {
        setNewItems((prev) => [
            ...prev,
            { ref: "", name: "", brand: "", count: 0, existing: false, priceCost: 0, iva: 23 },
        ]);
    };

    const handleRemoveRow = (index) => {
        setNewItems((prev) => prev.filter((_, i) => i !== index));
    };

    const handleChangeRow = (index, field, value) => {
        setNewItems((prev) => {
            const updated = [...prev];
            updated[index] = {
                ...updated[index],
                [field]: field === "priceCost" || field === "iva"
                    ? parseFloat(value) || 0  // Use parseFloat to safely convert to number
                    : value,
            };
            return updated;
        });
    };

    // When the reference field loses focus, trigger the lazy query
    const handleReferenceBlur = async (index, refValue) => {
        if (!refValue.trim()) return; // Do nothing for empty ref

        try {
            const { data } = await getItemByRef({
                variables: { ref: refValue }
            });

            if (data?.itemByRef) {
                setNewItems((prev) => {
                    const updated = [...prev];
                    updated[index] = {
                        ...updated[index],
                        name: data.itemByRef.name,
                        brand: data.itemByRef.brand,
                        existing: true,
                        _id: data.itemByRef._id, // Ensure the unique identifier is stored
                        priceCost: data.itemByRef.priceCost || updated[index].priceCost,
                        iva: data.itemByRef.iva || updated[index].iva
                    };
                    return updated;
                });
            }
        } catch (error) {
            console.error("Error querying item by ref:", error.message);
        }
    };

    // Submit function for adding multiple items
    const handleSubmitMultiple = async () => {
        // Remove supplier info validation
        // Validate each item in the newItems array
        for (const item of newItems) {
            if (
                !item.ref ||
                !item.name ||
                !item.brand ||
                item.count <= 0 ||
                item.priceCost <= 0 ||  // Adjusted to allow only valid positive values
                item.iva < 0            // Allow IVA to be zero, if needed
            ) {
                alert(t("ITEMS.VALIDATION_ALL_FIELDS"));
                return;
            }
        }

        const addedItems = [];

        // Process each item: use addStock if the item exists, otherwise create a new item.
        for (const item of newItems) {
            if (item.existing && item._id) {
                try {
                    // Call mutation to add stock for the existing item.
                    await addStock({
                        variables: { id: item._id, count: Number(item.count) },
                    });
                    addedItems.push({
                        ref: item.ref,
                        name: item.name,
                        brand: item.brand,
                        count: item.count,
                        priceCost: item.priceCost,
                        iva: item.iva,
                    });
                } catch (error) {
                    console.error("Error adding stock for existing item:", error.message);
                }
            } else {
                try {
                    // Prepare input and create a new item.
                    const createItemInput = {
                        ref: item.ref,
                        name: item.name,
                        brand: item.brand,
                        count: Number(item.count),
                        priceCost: Number(item.priceCost),  // Convert to number
                        iva: Number(item.iva),              // Convert to number
                        // Only include supplier info if it exists
                        ...(includeSupplierInfo && supplierInfo.supplier && {
                            supplier: supplierInfo.supplier,
                            supplier_nif: supplierInfo.supplier_nif,
                        }),
                    };

                    const res = await createItem({
                        variables: { input: createItemInput },
                    });
                    const newCreatedItem = res.data.createItem;
                    addedItems.push({
                        ref: newCreatedItem.ref,
                        name: newCreatedItem.name,
                        brand: newCreatedItem.brand,
                        count: newCreatedItem.count,
                        priceCost: newCreatedItem.priceCost,
                        iva: newCreatedItem.iva,
                    });
                } catch (error) {
                    console.error("Error creating new item:", error.message);
                }
            }
        }

        // Reset form fields
        setNewItems([{ ref: "", name: "", brand: "", count: 0, existing: false, priceCost: 0, iva: 0 }]);
        setSupplierInfo({ supplier: "", supplier_nif: "", fatNr: "", date: new Date().toISOString().split('T')[0] });
        refetch();

        // Generate PDF report with the added items
        generatePDF(addedItems);
    };

    // Only refetch when the search term changes
    useEffect(() => {
        refetch();
    }, [term, refetch]);

    const [includeSupplierInfo, setIncludeSupplierInfo] = useState(false);

    return (
        <Box p={3} bgcolor="#f5f5f5" minHeight="100vh">
                    <Grid
	                    container
	                    direction="row"
	                    justifyContent="center"
	                    spacing={3}
	                    sx={{ maxWidth: 1400, margin: "0 auto" }}
	                >
	                    <Grid item xs={12} md={12} lg={10}>{/* Search Bar */}
                    <Box mb={3}>
                        <Paper
                            component="form"
                            sx={{
                                p: "2px 4px",
                                display: "flex",
                                alignItems: "center",
                                width: "100%",
                                boxShadow: 1,
                            }}
                        >
                            <IconButton sx={{ p: "10px" }} aria-label="search">
                                <SearchIcon />
                            </IconButton>
                            <Divider sx={{ height: 28, m: 0.5 }} orientation="vertical" />
                            <TextField
                                variant="standard"
                                placeholder={t("ACTIONS.SEARCH")}
                                onChange={(e) => debouncedSetTerm(e.target.value)}
                                fullWidth
                                InputProps={{ disableUnderline: true }}
                            />
                        </Paper>
                    </Box>

                    {/* Supplier Information */}
                    <Paper elevation={3} sx={{ padding: 3, mb: 4, backgroundColor: "#ffffff" }}>
                        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                            <Typography variant="h6" color="textPrimary" fontWeight="bold">
                                {t("ITEMS.SUPPLIER_INFO")}
                            </Typography>
                            <FormControlLabel
                                control={
                                    <CustomSwitch
                                        checked={includeSupplierInfo}
                                        onChange={(e) => setIncludeSupplierInfo(e.target.checked)}
                                    />
                                }
                                label={<span style={{ fontSize: '16px', fontWeight: 500 }}>{t("COMMON.SHOW_FIELDS")}</span>}
                            />
                        </Box>

                        {includeSupplierInfo && (
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        fullWidth
                                        label={t("FIELDS.supplier")}
                                        variant="outlined"
                                        value={supplierInfo.supplier}
                                        onChange={(e) =>
                                            setSupplierInfo((prev) => ({ ...prev, supplier: e.target.value }))
                                        }
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        fullWidth
                                        label={t("FIELDS.supplierNif")}
                                        variant="outlined"
                                        value={supplierInfo.supplier_nif}
                                        onChange={(e) =>
                                            setSupplierInfo((prev) => ({ ...prev, supplier_nif: e.target.value }))
                                        }
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        fullWidth
                                        label={t("FIELDS.invoiceNumber")}
                                        variant="outlined"
                                        value={supplierInfo.fatNr}
                                        onChange={(e) =>
                                            setSupplierInfo((prev) => ({ ...prev, fatNr: e.target.value }))
                                        }
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        fullWidth
                                        label={t("FIELDS.date")}
                                        type="date"
                                        variant="outlined"
                                        value={supplierInfo.date}
                                        onChange={(e) =>
                                            setSupplierInfo((prev) => ({ ...prev, date: e.target.value }))
                                        }
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                    />
                                </Grid>
                            </Grid>
                        )}
                    </Paper>

                    {/* Items Form */}
                    <Paper elevation={3} sx={{ padding: 3, mb: 4, backgroundColor: "#ffffff" }}>
                        <Typography variant="h6" gutterBottom color="textPrimary" fontWeight="bold">
                            {t("ITEMS.CREATE_ADD_ITEMS")}
                        </Typography>
                        {newItems.map((item, index) => (
                            <Box key={index} sx={{ mb: 2 }}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={3}>
                                        <TextField
                                            fullWidth
                                            label={t("FIELDS.ref")}
                                            variant="outlined"
                                            value={item.ref}
                                            onChange={(e) => handleChangeRow(index, "ref", e.target.value)}
                                            onBlur={(e) => handleReferenceBlur(index, e.target.value)}
                                            // Add loading indicator
                                            InputProps={{
                                                endAdornment: refLoading && (
                                                    <CircularProgress size={20} />
                                                )
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={3}>
                                        <TextField
                                            fullWidth
                                            label={t("FIELDS.name")}
                                            variant="outlined"
                                            value={item.name}
                                            onChange={(e) => handleChangeRow(index, "name", e.target.value)}
                                            disabled={item.existing}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={3}>
                                        <TextField
                                            fullWidth
                                            label={t("FIELDS.brand")}
                                            variant="outlined"
                                            value={item.brand}
                                            onChange={(e) => handleChangeRow(index, "brand", e.target.value)}
                                            disabled={item.existing}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={2}>
                                        <TextField
                                            fullWidth
                                            label={t("FIELDS.count")}
                                            type="number"
                                            variant="outlined"
                                            value={item.count}
                                            onChange={(e) => handleChangeRow(index, "count", e.target.value)}
                                            inputProps={{ min: 0, step: "0.01" }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={1} display="flex" alignItems="center">
                                        {newItems.length > 1 && (
                                            <IconButton color="error" onClick={() => handleRemoveRow(index)}>
                                                <RemoveIcon />
                                            </IconButton>
                                        )}
                                    </Grid>
                                </Grid>
                                {/* Price and IVA fields */}
                                <Grid container spacing={2} sx={{ mt: 1 }}>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label={t("FIELDS.costPrice")}
                                            variant="outlined"
                                            type="number"
                                            value={item.priceCost}
                                            onChange={(e) => handleChangeRow(index, "priceCost", parseFloat(e.target.value) || 0)}
                                            inputProps={{ min: 0, step: "0.01" }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label={t("FIELDS.iva")}
                                            variant="outlined"
                                            type="number"
                                            value={item.iva}
                                            onChange={(e) => handleChangeRow(index, "iva", parseFloat(e.target.value) || 0)}
                                            inputProps={{ min: 0, step: "1" }}
                                        />
                                    </Grid>

                                </Grid>
                            </Box>
                        ))}
                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<AddIcon />}
                            onClick={handleAddRow}
                            sx={{ mb: 2 }}
                        >
                            {t("ACTIONS.ADD_ROW")}
                        </Button>
                        <Button
                            fullWidth
                            onClick={handleSubmitMultiple}
                            variant="contained"
                            color="primary"
                            size="large"
                            startIcon={<AddIcon />}
                            sx={{
                                py: 1.5,
                                boxShadow: 2,
                                "&:hover": { boxShadow: 4 },
                            }}
                        >
                            {t("ACTIONS.SAVE_ITEMS")}
                        </Button>
                    </Paper>

                    {/* Export to Excel */}
                    <Box mb={2} display="flex" justifyContent="flex-end">
                        <Button variant="outlined" color="primary" onClick={handleExportExcel}>
                            {t("ACTIONS.EXPORT_EXCEL")}
                        </Button>
                    </Box>

                    {/* Items List */}
                    {loading ? (
                        <Box display="flex" justifyContent="center" alignItems="center" height="50vh">
                            <CircularProgress />
                        </Box>
                    ) : (
                        <Paper elevation={3} sx={{ padding: 2, backgroundColor: "#ffffff" }}>
                            <Typography variant="h6" gutterBottom color="textPrimary" fontWeight="bold">
                                {t("ITEMS.LIST_TITLE")}
                            </Typography>
                            <List>
                                {data?.items?.length === 0 ? (
                                    <Typography variant="body1" color="textSecondary" align="center">
                                        {t("ITEMS.EMPTY")}
                                    </Typography>
                                ) : (
                                    data.items.map((item) => (
                                        <ListItem
                                            key={item._id}
                                            sx={{
                                                borderRadius: 1,
                                                mb: 1,
                                                boxShadow: 1,
                                                transition: "background-color 0.3s",
                                                "&:hover": { backgroundColor: "#f0f0f0" },
                                            }}
                                        >
                                            <ListItemText
                                                primary={<Typography variant="subtitle1" fontWeight="bold">{item?.name}</Typography>}
                                                secondary={
                                                    <>
                                                        <Typography variant="body2" color="textSecondary">
                                                            {t("FIELDS.brand")}: {item.brand}
                                                        </Typography>
                                                        <Typography variant="body2" color="textSecondary">
                                                            {t("FIELDS.ref")}: {item.ref}
                                                        </Typography>
                                                        <Typography variant="body2" color="textSecondary">
                                                            {t("FIELDS.count")}: {item.count}
                                                        </Typography>
                                                    </>
                                                }
                                            />
                                            <ListItemSecondaryAction>
                                                <Tooltip title={t("ITEMS.ADD_STOCK")}>
                                                    <IconButton edge="end" color="primary" onClick={() => handleOpen(item._id, "add")}>
                                                        <AddIcon />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title={t("ITEMS.REMOVE_STOCK")}>
                                                    <IconButton edge="end" color="error" onClick={() => handleOpen(item._id, "remove")}>
                                                        <RemoveIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </ListItemSecondaryAction>
                                        </ListItem>
                                    ))
                                )}
                            </List>
                        </Paper>
                    )}

                    {/* Stock Update Dialog */}
                    <Dialog open={open} onClose={handleClose}>
                        <DialogTitle>{actionType === "add" ? t("ITEMS.ADD_STOCK") : t("ITEMS.REMOVE_STOCK")}</DialogTitle>
                        <DialogContent>
                            <TextField
                                autoFocus
                                margin="dense"
                                label={t("FIELDS.count")}
                                type="number"
                                fullWidth
                                variant="outlined"
                                value={dialogCount}
                                onChange={(e) => setDialogCount(e.target.value)}
                                inputProps={{ min: 0.01, step: "0.01" }}
                            />
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={handleClose} color="secondary" variant="outlined">
                                {t("ACTIONS.CANCEL")}
                            </Button>
                            <Button
                                onClick={handleStockUpdate}
                                color={actionType === "add" ? "primary" : "error"}
                                variant="contained"
                            >
                                {actionType === "add" ? t("ACTIONS.ADD") : t("ACTIONS.REMOVE")}
                            </Button>
                        </DialogActions>
                    </Dialog>
                </Grid>
            </Grid>
        </Box>
    );
}

export default ItemManager;
